// @flow
import React, { useRef, useEffect, forwardRef } from 'react';
import {
    useTable,
    useSortBy,
    usePagination,
    useRowSelect,
    useGlobalFilter,
    useAsyncDebounce,
    useExpanded,
} from 'react-table';
import classNames from 'classnames';

// components
import Pagination from './Pagination';

// Define a default UI for filtering
const GlobalFilter = ({ preGlobalFilteredRows, globalFilter, setGlobalFilter, searchBoxClass, serverSearchValue, onServerSearchChange, isSearching }) => {
    const count = preGlobalFilteredRows ? preGlobalFilteredRows.length : 0;
    const [value, setValue] = React.useState(serverSearchValue !== undefined ? serverSearchValue : globalFilter);

    React.useEffect(() => {
        if (serverSearchValue !== undefined) {
            setValue(serverSearchValue);
        }
    }, [serverSearchValue]);

    const onChange = useAsyncDebounce((value) => {
        if (onServerSearchChange) {
            onServerSearchChange(value);
        } else {
            setGlobalFilter(value || undefined);
        }
    }, 150);

    return (
        <div className={classNames(searchBoxClass)}>
            <span className="d-flex align-items-center">
                Search :{' '}
                <input
                    value={value || ''}
                    onChange={(e) => {
                        setValue(e.target.value);
                        onChange(e.target.value);
                    }}
                    placeholder={'Search Records...'}
                    className="form-control w-auto ms-1"
                />
                {isSearching && (
                    <span
                        className="ms-2"
                        style={{
                            display: 'inline-block',
                            width: '18px',
                            height: '18px',
                            border: '2px solid #dee2e6',
                            borderTop: '2px solid #727cf5',
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite',
                        }}
                        title="Searching..."
                    />
                )}
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </span>
        </div>
    );
};

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
    const defaultRef = useRef();
    const resolvedRef = ref || defaultRef;

    useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
        <>
            <div className="form-check">
                <input type="checkbox" className="form-check-input" ref={resolvedRef} {...rest} />
                <label htmlFor="form-check-input" className="form-check-label"></label>
            </div>
        </>
    );
});

const Table = (props) => {
    const { extraButtons, inputRef, handleFileChange, serverSearchValue, onServerSearchChange, isSearching } = props;
    const isSearchable = props['isSearchable'] || false;
    const isSortable = props['isSortable'] || false;
    const pagination = props['pagination'] || false;
    const isSelectable = props['isSelectable'] || false;
    const isExpandable = props['isExpandable'] || false;

    const dataTable = useTable(
        {
            columns: props['columns'],
            data: props['data'],
            initialState: { pageSize: props['pageSize'] || 10 },
            pageCount: props['pageCount'],
        },
        isSearchable && !onServerSearchChange && useGlobalFilter,
        isSortable && useSortBy,
        isExpandable && useExpanded,
        pagination && usePagination,
        isSelectable && useRowSelect,
        (hooks) => {
            isSelectable &&
                hooks.visibleColumns.push((columns) => [
                    // Let's make a column for selection
                    {
                        id: 'selection',
                        // The header can use the table's getToggleAllRowsSelectedProps method
                        // to render a checkbox
                        Header: ({ getToggleAllPageRowsSelectedProps }) => (
                            <div>
                                <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
                            </div>
                        ),
                        // The cell can use the individual row's getToggleRowSelectedProps method
                        // to the render a checkbox
                        Cell: ({ row }) => (
                            <div>
                                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                            </div>
                        ),
                    },
                    ...columns,
                ]);

            isExpandable &&
                hooks.visibleColumns.push((columns) => [
                    // Let's make a column for selection
                    {
                        // Build our expander column
                        id: 'expander', // Make sure it has an ID
                        Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
                            <span {...getToggleAllRowsExpandedProps()}>{isAllRowsExpanded ? '-' : '+'}</span>
                        ),
                        Cell: ({ row }) =>
                            // Use the row.canExpand and row.getToggleRowExpandedProps prop getter
                            // to build the toggle for expanding a row
                            row.canExpand ? (
                                <span
                                    {...row.getToggleRowExpandedProps({
                                        style: {
                                            // We can even use the row.depth property
                                            // and paddingLeft to indicate the depth
                                            // of the row
                                            paddingLeft: `${row.depth * 2}rem`,
                                        },
                                    })}>
                                    {row.isExpanded ? '-' : '+'}
                                </span>
                            ) : null,
                    },
                    ...columns,
                ]);
        }
    );

    let rows = pagination ? dataTable.page : dataTable.rows;

    return (
        <>
            {isSearchable && (
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
                    <GlobalFilter
                        preGlobalFilteredRows={dataTable.preGlobalFilteredRows}
                        globalFilter={dataTable.state.globalFilter}
                        setGlobalFilter={dataTable.setGlobalFilter}
                        searchBoxClass={props['searchBoxClass']}
                        serverSearchValue={serverSearchValue}
                        onServerSearchChange={onServerSearchChange}
                        isSearching={isSearching}
                    />

                    {(extraButtons || inputRef || handleFileChange) && (
                        <div className="d-flex align-items-center gap-2">
                            {inputRef && handleFileChange && (
                                <input
                                    style={{ display: 'none' }}
                                    ref={inputRef}
                                    type="file"
                                    onChange={handleFileChange}
                                />
                            )}
                            {extraButtons}
                        </div>
                    )}
                </div>
            )}

            <div className="table-responsive">
                <table
                    {...dataTable.getTableProps()}
                    className={classNames('table table-centered react-table table-striped', props['tableClass'])}>
                    <thead className={props['theadClass']}>
                        {dataTable.headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th
                                        {...column.getHeaderProps(column.sort && column.getSortByToggleProps())}
                                        className={classNames({
                                            sorting_desc: column.isSortedDesc === true,
                                            sorting_asc: column.isSortedDesc === false,
                                            sortable: column.sort === true,
                                        })}
                                        style={{
                                            width: column.width ? `${column.width}px` : undefined,
                                            minWidth: column.minWidth ? `${column.minWidth}px` : undefined,
                                            maxWidth: column.maxWidth ? `${column.maxWidth}px` : undefined,
                                        }}>
                                        {column.render('Header')}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...dataTable.getTableBodyProps()}>
                        {(rows || []).map((row, i) => {
                            dataTable.prepareRow(row);
                            return (
                                <tr 
                                    {...row.getRowProps()}
                                    className={classNames(row.original?.isCategory && 'category-row', row.getRowProps().className)}
                                >
                                    {row.cells.map((cell) => {
                                        return (
                                            <td 
                                                {...cell.getCellProps()}
                                                style={{
                                                    width: cell.column.width ? `${cell.column.width}px` : undefined,
                                                    minWidth: cell.column.minWidth ? `${cell.column.minWidth}px` : undefined,
                                                    maxWidth: cell.column.maxWidth ? `${cell.column.maxWidth}px` : undefined,
                                                    wordWrap: 'break-word',
                                                    whiteSpace: 'normal',
                                                    height: 'auto',
                                                    verticalAlign: 'top',
                                                }}>
                                                {cell.render('Cell')}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {pagination && (
                <Pagination
                    tableProps={dataTable}
                    sizePerPageList={props['sizePerPageList']}
                    pageCounts={props['pageCount']}
                    pageNumber={props['pageNumber']}
                    totalCount={props['totalCount']}
                    onPageChange={props.onPageChange}
                    onPageClick={props.onPageClick}
                />
            )}
        </>
    );
};

export default Table;
